import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchPdfData } from "../services/api";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faPrint,
  faFileInvoice,
  faSpinner,
  faTriangleExclamation,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";

/* ─── Styles injected once ──────────────────────────────────────────────── */
const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .llc-root {
    --navy:        #0f1f5c;
    --navy-hover:  #0a1540;
    --navy-bg:     #eef1fb;
    --green:       #00b86b;
    --green-hover: #009a59;
    --green-bg:    #e6f9f1;
    --red:         #dc2626;
    --red-bg:      #fef2f2;
    --border:      #e2e6f3;
    --surface:     #f4f6fc;
    --white:       #ffffff;
    --text:        #111827;
    --muted:       #6b7280;
    --radius:      10px;
    --radius-sm:   7px;
    --shadow-sm:   0 1px 3px rgba(15,31,92,0.06), 0 1px 2px rgba(15,31,92,0.04);
    --shadow-md:   0 4px 20px rgba(15,31,92,0.10);
    --font:        'DM Sans', system-ui, sans-serif;
    --font-mono:   'DM Mono', monospace;

    font-family: var(--font);
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--surface);
    overflow: hidden;
  }

  /* ── Header ── */
  .llc-header {
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    background: var(--navy);
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
    gap: 16px;
  }

  .llc-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .llc-brand-logo {
    width: 30px;
    height: 30px;
    border-radius: var(--radius-sm);
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .llc-brand-name {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.02em;
  }
  .llc-divider {
    width: 1px;
    height: 20px;
    background: rgba(255,255,255,0.15);
    flex-shrink: 0;
  }

  /* doc pill */
  .llc-doc-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 99px;
    padding: 4px 12px 4px 8px;
    min-width: 0;
  }
  .llc-doc-icon {
    width: 22px;
    height: 22px;
    border-radius: 5px;
    background: var(--green);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .llc-doc-label {
    font-size: 12px;
    font-weight: 500;
    color: rgba(255,255,255,0.80);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }

  /* ── Header actions ── */
  .llc-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .llc-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 99px;
    letter-spacing: 0.06em;
    font-family: var(--font-mono);
  }
  .llc-badge-loading {
    color: #92400e;
    background: #fef3c7;
  }

  /* ── Buttons ── */
  .llc-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 14px;
    font-size: 12px;
    font-weight: 600;
    font-family: var(--font);
    letter-spacing: 0.01em;
    border-radius: var(--radius-sm);
    cursor: pointer;
    border: none;
    transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
    white-space: nowrap;
    text-decoration: none;
  }
  .llc-btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none !important; }
  .llc-btn:not(:disabled):hover { transform: translateY(-1px); }

  .llc-btn-ghost {
    color: rgba(255,255,255,0.85);
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.14);
  }
  .llc-btn-ghost:not(:disabled):hover {
    background: rgba(255,255,255,0.14);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }

  .llc-btn-green {
    color: #fff;
    background: var(--green);
  }
  .llc-btn-green:not(:disabled):hover {
    background: var(--green-hover);
    box-shadow: 0 3px 10px rgba(0,184,107,0.30);
  }

  /* ── Main body ── */
  .llc-body {
    flex: 1;
    overflow: hidden;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /* Info strip (order ID) */
  .llc-infostrip {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-sm);
    flex-shrink: 0;
  }
  .llc-infostrip-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--green);
    flex-shrink: 0;
  }
  .llc-infostrip-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--muted);
  }
  .llc-infostrip-value {
    font-size: 12px;
    font-weight: 600;
    color: var(--navy);
    font-family: var(--font-mono);
  }

  /* ── Viewer card ── */
  .llc-viewer-card {
    flex: 1;
    overflow: hidden;
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-md);
    display: flex;
    flex-direction: column;
    position: relative;
  }
  .llc-viewer-inner {
    flex: 1;
    overflow: auto;
    height: 100%;
  }

  /* ── Loading screen ── */
  .llc-state-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 18px;
    padding: 32px;
  }
  .llc-state-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .llc-state-icon-loading { background: var(--navy-bg); }
  .llc-state-icon-error   { background: var(--red-bg); }

  .llc-state-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text);
    text-align: center;
  }
  .llc-state-sub {
    font-size: 13px;
    color: var(--muted);
    text-align: center;
    max-width: 280px;
    line-height: 1.6;
    margin-top: 4px;
  }

  /* progress bar for loading */
  .llc-progress-track {
    width: 200px;
    height: 3px;
    background: var(--border);
    border-radius: 99px;
    overflow: hidden;
    margin-top: 8px;
  }
  .llc-progress-bar {
    height: 100%;
    background: var(--navy);
    border-radius: 99px;
    animation: llc-progress 1.8s ease-in-out infinite;
  }
  @keyframes llc-progress {
    0%   { width: 0%;   margin-left: 0%; }
    50%  { width: 60%;  margin-left: 20%; }
    100% { width: 0%;   margin-left: 100%; }
  }

  .llc-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 8px;
    padding: 8px 20px;
    background: var(--navy);
    color: #fff;
    border: none;
    border-radius: var(--radius-sm);
    font-size: 13px;
    font-weight: 600;
    font-family: var(--font);
    cursor: pointer;
    transition: background 0.15s;
  }
  .llc-retry-btn:hover { background: var(--navy-hover); }

  /* react-pdf-viewer toolbar override — keep it compact */
  .rpv-toolbar { min-height: 40px !important; }
  .rpv-core__viewer { border-radius: 0 0 var(--radius) var(--radius); }
`;

/* ─── Inject styles once ────────────────────────────────────────────────── */
function injectStyles() {
  if (document.getElementById("llc-pdf-style")) return;
  const el = document.createElement("style");
  el.id = "llc-pdf-style";
  el.textContent = STYLE;
  document.head.appendChild(el);
}

/* ─── Loading Screen ────────────────────────────────────────────────────── */
const LoadingScreen = () => (
  <div className="llc-state-screen">
    <div className="llc-state-icon llc-state-icon-loading">
      <FontAwesomeIcon
        icon={faSpinner}
        spin
        style={{ fontSize: 22, color: "var(--navy)" }}
      />
    </div>
    <div style={{ textAlign: "center" }}>
      <p className="llc-state-title">Preparing your invoice</p>
      <p className="llc-state-sub">Generating the PDF document, this will only take a moment.</p>
    </div>
    <div className="llc-progress-track">
      <div className="llc-progress-bar" />
    </div>
  </div>
);

/* ─── Error Screen ──────────────────────────────────────────────────────── */
const ErrorScreen = ({ message, onRetry }) => (
  <div className="llc-state-screen">
    <div className="llc-state-icon llc-state-icon-error">
      <FontAwesomeIcon
        icon={faTriangleExclamation}
        style={{ fontSize: 22, color: "var(--red)" }}
      />
    </div>
    <div style={{ textAlign: "center" }}>
      <p className="llc-state-title">Could not load document</p>
      <p className="llc-state-sub">{message || "Something went wrong while fetching the PDF. Please try again."}</p>
    </div>
    <button className="llc-retry-btn" onClick={onRetry}>
      <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: 12 }} />
      Try again
    </button>
  </div>
);

/* ─── Toolbar Button ────────────────────────────────────────────────────── */
const Btn = ({ icon, label, onClick, disabled, variant = "ghost" }) => (
  <button
    className={`llc-btn llc-btn-${variant}`}
    onClick={onClick}
    disabled={disabled}
  >
    <FontAwesomeIcon icon={icon} style={{ fontSize: 11 }} />
    {label}
  </button>
);

/* ─── Main Component ────────────────────────────────────────────────────── */
const PdfViewerPage = () => {
  const navigate      = useNavigate();
  const location      = useLocation();
  const myQueryParam  = new URLSearchParams(location.search).get("oid");

  const [pdfUrl,  setPdfUrl]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  injectStyles();

  const base64ToBlob = (base64, contentType) => {
    const chars    = atob(base64);
    const bytes    = new Uint8Array(chars.length);
    for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
    return new Blob([bytes], { type: contentType });
  };

  const loadPdf = useCallback(async () => {
    if (!myQueryParam) {
      setError("No document ID found in the URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response    = await fetchPdfData(myQueryParam);
      const base64String = response.data.responseObject;
      const blob        = base64ToBlob(base64String, "application/pdf");
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error("PDF load error:", err);
      setError(err?.message || "Unable to fetch the document.");
    } finally {
      setLoading(false);
    }
  }, [myQueryParam]);

  useEffect(() => {
    loadPdf();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [loadPdf]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a  = document.createElement("a");
    a.href   = pdfUrl;
    a.download = `LLC-Invoice-${myQueryParam || "doc"}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const win = window.open(pdfUrl);
    if (win) win.onload = () => win.print();
  };

  return (
    <div className="llc-root">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="llc-header">

        {/* Left: brand + doc pill */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div className="llc-brand">
            {/* <div className="llc-brand-logo">
              <FontAwesomeIcon
                icon={faFileInvoice}
                style={{ fontSize: 14, color: "#fff" }}
              />
            </div> */}
            <span className="llc-brand-name">Life Lab Care</span>
          </div>

          <div className="llc-divider" />

          <div className="llc-doc-pill">
            <div className="llc-doc-icon">
              <FontAwesomeIcon
                icon={faFileInvoice}
                style={{ fontSize: 11, color: "#fff" }}
              />
            </div>
            <span className="llc-doc-label">Tax Invoice</span>
          </div>

          {loading && (
            <span className="llc-badge llc-badge-loading">LOADING</span>
          )}
        </div>

        {/* Right: actions */}
        <div className="llc-actions">
          <Btn
            icon={faArrowLeft}
            label="Back to Payment"
            onClick={() => navigate(`/invoice?oid=${myQueryParam}`)}
            variant="ghost"
          />
          <Btn
            icon={faDownload}
            label="Download"
            onClick={handleDownload}
            disabled={!pdfUrl || loading}
            variant="ghost"
          />
          <Btn
            icon={faPrint}
            label="Print"
            onClick={handlePrint}
            disabled={!pdfUrl || loading}
            variant="green"
          />
        </div>
      </header>

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <main className="llc-body">

        {/* Viewer card */}
        <div className="llc-viewer-card">
          {loading && <LoadingScreen />}

          {!loading && error && (
            <ErrorScreen message={error} onRetry={loadPdf} />
          )}

          {!loading && !error && pdfUrl && (
            <div className="llc-viewer-inner">
              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <Viewer
                  fileUrl={pdfUrl}
                  plugins={[defaultLayoutPluginInstance]}
                />
              </Worker>
            </div>
          )}
        </div>

      </main>
    </div>
  );
};

export default PdfViewerPage;